import { useState, useEffect } from "react"
import Button from "aldoo-ra/Button"
import Image from "aldoo-ra/Image"
import MenuGroup from "aldoo-ra/MenuGroup"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import PubSub from "pubsub-js"
import Value from "aldoo-ra/Value"
import SiteLanguageSelector from "../SiteLanguageSelector"
import ThemeSwitcher from "aldoo-ra/ThemeSwitcher"
import { navigate } from "vike/client/router"
import { usePageContext } from "vike-react/usePageContext"
import Localization from "aldoo-ra/Localization"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import { getStoreAccount } from "aldoo-ra/Store/api"
import { RocketLaunchIcon } from "@heroicons/react/24/solid"
import AccountButton from "./AccountButton"

function scrollToId(id) {
  const el = document.getElementById(id)
  if (el) { el.scrollIntoView({ behavior: "instant" }); return }
  let attempts = 0
  const poll = () => {
    const el = document.getElementById(id)
    if (el) { el.scrollIntoView({ behavior: "instant" }); return }
    if (++attempts < 120) requestAnimationFrame(poll)
  }
  requestAnimationFrame(poll)
}

export default function NavbarDesktop({ onContactClick }) {
  const pageContext = usePageContext()
  const currentPath = pageContext?.urlPathname || (typeof window !== "undefined" ? window.location.pathname : "/")
  const { currentLanguage } = Localization()
  const localeRoot = currentLanguage && currentLanguage !== "en" ? `/${currentLanguage}` : "/"
  const isHome = currentPath === localeRoot || currentPath === localeRoot + "/"
  const showTransferNow = /\/(pricing|features)\/?$/.test(currentPath)
  const texts = TextBundle({ id: "signin" })
  const [isScrolled, setIsScrolled] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [auth] = PlatformAuth.useAuth()
  const [profile] = Value("user-profile")
  const [isPro, setIsPro] = useState(true) // assume Pro until checked (avoids upsell flicker)


  useEffect(() => {
    setMounted(true)
    const handleScroll = () => setIsScrolled(window.scrollY > 10)
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    if (!auth) { setIsPro(true); return }
    const check = () => getStoreAccount()
      .then((res) => { const acc = res?.result || res; setIsPro((acc?.account_type || "free") === "pro") })
      .catch(() => {})
    check()
    const t = PubSub.subscribe("payment-success", check)
    return () => PubSub.unsubscribe(t)
  }, [auth])

  const initial = (profile?.name || auth?.email || "?")[0].toUpperCase()

  return (
    <header
      className={`fixed top-0 left-0 right-0 w-full z-50 transition-shadow duration-300 ${
        isScrolled ? "shadow-md bg-paper/50 backdrop-blur-lg" : "bg-transparent"
      } ${isScrolled ? "border-b border-bordered" : ""}`}
    >
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="h-16 flex items-center gap-6">
          {/* Logo */}
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault()
              if (isHome) {
                PubSub.publish("file-share-reset")
              } else {
                navigate("/")
              }
            }}
            className="flex items-center gap-1.5 shrink-0"
          >
            <Image
              id="aldoo_transfer_logo_icon"
              className="h-[30px] w-auto object-contain"
            />
            <span className="text-text font-bold text-xl tracking-tight">
              Aldoo <span className="text-primary_hov">Transfer</span>
            </span>
          </a>

          {/* Central nav */}
          <nav className="hidden md:flex items-center gap-1">
            {mounted && (
              <MenuGroup
                name={auth ? "HeaderMenuAuth" : "HeaderMenuPublic"}
                renderAs="hlist"
                itemWidth="auto"
                itemsAsLinks={false}
                styleOverrides={{
                  menu: "flex flex-row items-start py-0 my-0",
                  menu_item: "flex items-center list-none cursor-pointer px-0 py-0 my-0",
                }}
                menuItemRender={(item) => {
                  const url = item.link?.[0]?.url
                  const isContacts = item.name === "NavContacts"
                  const isCreate = item.name === "NavCreate"
                  const isHashLink = url?.startsWith("#") || url?.startsWith("/#")
                  const hashId = url?.startsWith("/#") ? url.slice(2) : url?.startsWith("#") ? url.slice(1) : null

                  if (isCreate) return null

                  const handleClick = () => {
                    if (isContacts) {
                      onContactClick()
                    } else if (isHashLink && hashId) {
                      const isOnHome = currentPath === localeRoot || currentPath === localeRoot + "/"
                      if (isOnHome) {
                        scrollToId(hashId)
                      } else {
                        navigate(localeRoot)
                        scrollToId(hashId)
                      }
                    } else if (url) {
                      navigate(url)
                    }
                  }

                  return (
                    <Button variant="nav_item" className="!text-white !font-normal" onClick={handleClick}>
                      {item?.label}
                    </Button>
                  )
                }}
              />
            )}
          </nav>

          {/* Right side */}
          <div className="ml-auto flex items-center gap-2">
            <SiteLanguageSelector
              menuPosition="bottom"
              showMenuMode="hover"
            />
            
            {mounted && showTransferNow && (
              <Button
                variant="primary"
                onClick={() => navigate(localeRoot)}
                className="!h-10 !min-h-0 !py-0 text-sm"
              >
                {texts?.transfer_now}
              </Button>
            )}

            {mounted && auth && !isPro && (
              <Button
                variant="primary_s_white"
                onClick={() => navigate("/pricing")}
                className="h-10 lg:h-10"
              >
                <RocketLaunchIcon className="w-4 h-4" />
                {texts?.getPro}
              </Button>
            )}
            
            <AccountButton mounted={mounted} auth={auth} initial={initial} profile={profile} texts={texts} />
          </div>

        </div>
      </div>
    </header>
  )
}